import type { MapDispatchToProps, MapStateToPropsFactory } from "react-redux";
import type { Dispatch } from "redux";

import { BET_OPTIONS, VALIDATION_TYPES } from "@ppb/betslip-core";
import {
  UI__BETSLIP_SBK_ACCA_INSURANCE_TOGGLE,
  UI__BETSLIP_SBK_EACH_WAY_TOGGLE,
  UI__BETSLIP_SBK_PRICE_BOOST_TOGGLE,
  type BetslipSportsbookAccaInsuranceToggleAction,
  type BetslipSportsbookEachWayToggleAction,
  type BetslipSportsbookPriceBoostToggleAction,
} from "@ppb/tbd-store/actions/betslip";
import {
  UI__BETSLIP_SBK_BONUS_BALL_TOGGLE,
  UI__BETSLIP_SBK_STAKE_INPUT_CHANGE,
  type BetslipSportsbookBonusBallToggleAction,
  type BetslipSportsbookStakeInputChange,
} from "../../store/state/actions";
import {
  BETTING__SBK_ACCA_INSURANCE_TOGGLE,
  BETTING__SBK_BONUS_BALL_TOGGLE,
  BETTING__SBK_EACH_WAY_TOGGLE,
  BETTING__SBK_GHOST_LEG_TOGGLE,
  BETTING__SBK_MONEY_BACK_TOGGLE,
  BETTING__SBK_PRICE_BOOST_TOGGLE,
  BETTING__SBK_STARTING_PRICE_TOGGLE,
  BETTING__SBK_UPDATE_COMBINATION_STAKE_ACTION,
  BETTING__SBK_VALIDATE_STAKE,
  BETTING__SPORTSBOOK_REMOVE_ALL_COMBINATION_FREE_BETS_WALLETS_ACTION,
  BettingSportsbookMoneyBackToggleAction,
  BettingSportsbookRemoveAllCombinationWalletsAction,
  BettingSportsbookRemoveGenerosityWalletsAction,
  UI__GENEROSITY_WALLET_REMOVE_CLICK,
  type BettingSportsbookAccaInsuranceToggleAction,
  type BettingSportsbookBonusBallToggleAction,
  type BettingSportsbookGhostLegToggleAction,
  type BettingSportsbookEachWayToggleAction,
  type BettingSportsbookPriceBoostToggleAction,
  type BettingSportsbookSPToggleAction,
  type BettingSportsbookUpdateCombinationStakeAction,
  type BettingSportsbookValidateStake,
} from "@ppb/tbd-store/actions/betting";
import { isStakeValid } from "../../helpers/territory-rules";
import type { BetslipApplicationState } from "../../store/state/BetslipApplicationState";
import { createOddsMovementSelector } from "../../store/state/betslip-module/betslip-odds-movement-selectors";
import { getSportsbookBettingState } from "@ppb/tbd-store/state/betting/sportsbook-betting/sportsbook-betting-selectors";
import { getBettingResolvers } from "../../store/state/sportsbook-betting/betting-resolver";
import {
  getBetslipGroup,
  getBetslipPlaceStatus,
  getSportsbookOddsMovement,
  getSportsbookConfirmationCombinations,
  getSportsbookConfirmationLegs,
  getSportsbookConfirmationRunners,
  getIsBetslipLoaded,
} from "../../store/state/betslip-module/betslip-module-selectors";
import type { BetControlsProps } from "./snowflakes/BetControls/BetControls.types";

import { currencyFormatWithDecimalPlaces } from "../../formatters/currency-adapter";
import { GenerosityWalletType } from "../../helpers/generosity-wallets";
import { createGenerosityPropsSelector } from "../generosity-mapper";
import { i18n } from "../../helpers/i18n";
import { buildAccaInsuranceLabels, buildCombinationOdds, buildCombinationPreviousOdds } from "../betslip-formatters";
import { translateMultiple } from "../connected-sportsbook-betslip-mapper";
import { createIsConfirmStep } from "../sportsbook-betslip-confirm-mapper";
import { buildBetOptions, buildSingleLegExtraDetails } from "./bet-controls-mapper";
import type {
  BetslipLocalization,
  SportsbookBetslipCapabilities,
  SportsbookBetslipConfig,
  SportsbookBetslipPreferences,
} from "../../contract";
import { toKeyboardSeparator } from "../../helpers/keyboard-helper";
import { emitBetslipGenerosityWalletRequested } from "../../contract/betslip-api";

export type BetControlsLabels = {
  odds: BetControlsProps["oddsLabel"];
  stake: BetControlsProps["stakeLabel"];
  generosityAlertRemoveLabel: BetControlsProps["generosityAlertRemoveLabel"];
} & Pick<
  BetControlsProps,
  "eachWayTitle" | "startingPriceTitle" | "bonusBallTitle" | "bonusBallSubtitle" | "oddsMovementUp" | "oddsMovementDown"
>;

type CardProps = {
  combinationIds: string[];
  labels: BetControlsLabels;
  isGenerosityDisabled: boolean;
  isSingleBetBetslip: boolean;
  hasStakeCaret: boolean;
  generosityType?: GenerosityWalletType;
  generosityAlertMessage?: string;
  isGenerosityWalletSelected: boolean;
} & Pick<
  BetControlsProps,
  | "betType"
  | "currencySymbol"
  | "bonusAvailabilityLabel"
  | "linesLabel"
  | "dividendBetLabel"
  | "formattedOriginalPotentialReturns"
  | "formattedPotentialReturns"
  | "returnsLabel"
  | "odds"
  | "oddsAccessibilityLabel"
  | "previousOdds"
  | "stake"
  | "separator"
  | "multiplier"
  | "hasBonusBall"
  | "bonusBallTitle"
  | "bonusBallSubtitle"
  | "isBonusBallSelected"
  | "isBonusBallDisabled"
  | "isBonusBallReadonly"
  | "hasEachWay"
  | "hasAccaInsurance"
  | "hasGenerosity"
  | "hasStartingPrice"
  | "isPanelDisabled"
  | "isStakeValid"
  | "isEachWaySelected"
  | "isPriceBoostSelected"
  | "isGenerositySelected"
  | "isStartingPriceSelected"
  | "isAccaInsuranceSelected"
  | "isAccaInsuranceReadonly"
  | "isAccaInsuranceDisabled"
  | "accaInsuranceTitle"
  | "accaInsuranceSubtitle"
  | "accaInsuranceTermsLabel"
  | "accaInsuranceTermsUrl"
  | "hintType"
  | "hintMessage"
  | "eachWaySubtitle"
  | "oddsMovement"
  | "displayReturns"
  | "generosityIconName"
>;

export type StateProps = CardProps | Record<string, never>;

export type ContainerProps = {
  combinationId: string;
  combinationIds: string[];
  isDividend?: boolean;
  hasAvailabilityHints?: boolean;
  shouldFocusStakeField?: boolean;
  title?: string;
  localization: BetslipLocalization;
  config: SportsbookBetslipConfig;
  capabilities: SportsbookBetslipCapabilities;
  preferences: SportsbookBetslipPreferences;
};

const buildBonusAvailabilityString = (
  isBonusSelected: boolean | undefined,
  isBonusAvailable: boolean,
  isBonusUsageIncompatible: boolean,
): string => {
  if (isBonusSelected && !isBonusAvailable) return i18n({ key: "I18N.BETSLIP.BONUS_NOT_AVAILABLE" });
  if (isBonusSelected && isBonusUsageIncompatible) return i18n({ key: "I18N.BETSLIP.FREE_BET_INELIGIBLE" });
  return "";
};

const buildOddsAccessibilityLabel = (label: string, odds: string | undefined) => `${label}: ${odds ?? ""}`.trim();

export const makeMapStateToProps: MapStateToPropsFactory<StateProps, ContainerProps, BetslipApplicationState> = () => {
  const labels = {
    odds: i18n({ key: "I18N.BETSLIP.ODDS" }),
    stake: i18n({ key: "I18N.BETSLIP.STAKE_PLACEHOLDER" }),
    eachWayTitle: i18n({ key: "I18N.BETSLIP.EACHWAY" }),
    startingPriceTitle: i18n({ key: "I18N.BETSLIP.TITLE.STARTING_PRICE" }),
    bonusBallTitle: i18n({ key: "I18N.BETSLIP.BONUS_BALL" }),
    bonusBallSubtitle: i18n({ key: "I18N.BETSLIP.BONUS_BALL_SUBTITLE" }),
    oddsMovementUp: i18n({ key: "I18N.BETSLIP.ODDS_MOVEMENT_INCREASE" }),
    oddsMovementDown: i18n({ key: "I18N.BETSLIP.ODDS_MOVEMENT_DECREASE" }),
    generosityAlertRemoveLabel: i18n({ key: "I18N.BETSLIP.REMOVE" }),
  };

  const getOddsMovement = createOddsMovementSelector();
  const getIsConfirmStep = createIsConfirmStep();
  const getGenerosityProps = createGenerosityPropsSelector();

  return (
    state: BetslipApplicationState,
    {
      combinationId,
      combinationIds,
      hasAvailabilityHints,
      isDividend,
      title,
      localization,
      config,
      capabilities,
      preferences,
    },
  ): StateProps => {
    const { currencySymbol } = localization;
    const accaInsuranceTermsUrl = config.termsUrls.accaInsurance;

    const ids = combinationIds?.length ? combinationIds : combinationId ? [combinationId] : [];

    const primaryCombinationId = ids[0];

    const isStakeInputCaretEnabled = capabilities.stakeInputCaret.enabled;
    const {
      combinations: bettingCombinations,
      validations: { combinations: validations },
      legs: bettingLegs,
      runners: bettingRunners,
      failures,
      isBonusSelected,
    } = getSportsbookBettingState(state);

    const isConfirmStep = getIsConfirmStep(state);
    const combinations = isConfirmStep ? getSportsbookConfirmationCombinations(state) : bettingCombinations;
    const isSingleBetBetslip = Object.keys(combinations).length === 1;

    const legs = isConfirmStep ? getSportsbookConfirmationLegs(state) : bettingLegs;
    const runners = isConfirmStep ? getSportsbookConfirmationRunners(state) : bettingRunners;
    const combination = combinations[primaryCombinationId];

    if (!getIsBetslipLoaded(state) || !combination) {
      return {};
    }

    const betOptions = buildBetOptions(combination, legs, runners);
    const { hasSomeStartingPriceLegSelected } = betOptions;

    const group = getBetslipGroup(state);
    const placeStatus = getBetslipPlaceStatus(state);
    const sportsbookOddsMovement = getSportsbookOddsMovement(state);

    const metadata = getBettingResolvers(group).getMetadata(state);
    const oddsMovementMap = getOddsMovement(sportsbookOddsMovement);

    const currentValidations = validations[combinationId] || [];
    const isBonusUsageIncompatible = currentValidations.some(
      ({ type }) =>
        type === VALIDATION_TYPES.BONUS_USAGE_ACCA_INSURANCE_INCOMPATIBLE ||
        type === VALIDATION_TYPES.BONUS_USAGE_PRICE_BOOST_INCOMPATIBLE,
    );

    const bonusAvailabilityLabel: string = buildBonusAvailabilityString(
      isBonusSelected,
      combination.isBonusAvailable,
      isBonusUsageIncompatible,
    );

    const isPlacing = placeStatus === "INPROGRESS";
    let odds: string | undefined;
    let linesLabel: string | undefined;

    if (combination.numLines === 1 && !isDividend) {
      odds = buildCombinationOdds(combination, preferences.oddsDisplay, legs);
    } else {
      linesLabel = !isDividend
        ? `${translateMultiple(combination.betType)} (x${combination.numLines})`
        : i18n({
            key: "I18N.BETSLIP.NUM_LINES",
            interpolationValues: {
              numLines: `${combination.numLines}`,
            },
          });
    }

    const singleDetails =
      combination.legs.length === 1
        ? buildSingleLegExtraDetails(combination, legs, failures.imply, metadata)
        : undefined;
    const hasHints = hasAvailabilityHints && singleDetails?.hasFailure;

    const isReturnWithValue = !hasSomeStartingPriceLegSelected && (combination?.potentialReturns || combination?.odds);
    const returnsLabel = isReturnWithValue
      ? i18n({ key: "I18N.BETSLIP.RETURNS" })
      : i18n({ key: "I18N.BETSLIP.BET_RETURNS_TBD" });

    let formattedPotentialReturns;
    let formattedOriginalPotentialReturns;

    const displayReturns = !isSingleBetBetslip;

    // Returns
    if (displayReturns && isReturnWithValue) {
      formattedPotentialReturns = currencyFormatWithDecimalPlaces({
        ...localization,
        value: combination.potentialReturns ?? 0,
        decimalPlaces: 2,
      });

      formattedOriginalPotentialReturns =
        combination?.originalPotentialReturns && combination.isPriceBoostSelected
          ? currencyFormatWithDecimalPlaces({
              ...localization,
              value: combination.originalPotentialReturns,
              decimalPlaces: 2,
            })
          : undefined;
    }

    const isPriceBoostSelected = !!combination.isPriceBoostSelected;

    const generosityProps = getGenerosityProps(state, {
      combination,
      combinationId,
      isSingleBetBetslip,
      localization,
      oddsDisplayPreference: preferences.oddsDisplay,
    });

    return {
      combinationIds: ids,
      betType: title,
      stake: combination.stake ?? undefined,
      separator: toKeyboardSeparator(localization.decimalSeparator),
      hasStartingPrice: betOptions.hasStartingPrice,
      hasEachWay: betOptions.hasEachWay,
      hasBonusBall: combination.availableOptions?.some((opt) => opt.optionType === BET_OPTIONS.BONUS_BALL) ?? false,
      isBonusBallSelected: combination.legs.some(
        (legId) => legs[legId].appliedOptions?.some((opt) => opt.optionType === BET_OPTIONS.BONUS_BALL),
      ),
      bonusBallTitle: labels.bonusBallTitle,
      bonusBallSubtitle: labels.bonusBallSubtitle,
      hasAccaInsurance: !!combination.isAccaInsuranceAvailable && !combination.accaInsuranceOffers?.length,
      isPanelDisabled: singleDetails?.hasFailure || isPlacing,
      isEachWaySelected: betOptions.isEachWaySelected,
      isAccaInsuranceSelected: !!combination.isAccaInsuranceSelected,
      isAccaInsuranceReadonly: isConfirmStep ? false : hasSomeStartingPriceLegSelected,
      isAccaInsuranceDisabled: isConfirmStep,
      isPriceBoostSelected,
      isStartingPriceSelected: betOptions.isStartingPriceSelected,
      isStakeValid: isStakeValid(localization, validations[combinationId]),
      multiplier: betOptions.multiplier,
      oddsMovement: hasSomeStartingPriceLegSelected ? undefined : oddsMovementMap[combinationId]?.movement,
      currencySymbol,
      bonusAvailabilityLabel,
      linesLabel,
      dividendBetLabel: isDividend
        ? i18n({
            key: "I18N.BETSLIP.DIVIDEND_BET",
          })
        : undefined,
      labels,
      eachWaySubtitle: singleDetails?.eachWaySubtitle,
      hintType: hasHints ? singleDetails?.hintType : undefined,
      hintMessage: hasHints ? singleDetails?.hintMessage : undefined,
      odds: singleDetails?.isClosed ? i18n({ key: "I18N.BETSLIP.NOT_AVAILABLE" }) : odds,
      oddsAccessibilityLabel: buildOddsAccessibilityLabel(labels.odds, odds),
      previousOdds: buildCombinationPreviousOdds(
        combination.displayOdds,
        singleDetails?.previousOdds,
        combination.isPriceBoostSelected,
        preferences.oddsDisplay,
      ),
      formattedOriginalPotentialReturns,
      formattedPotentialReturns,
      returnsLabel,
      displayReturns,
      accaInsuranceTermsUrl,
      isGenerosityDisabled: isConfirmStep,
      ...buildAccaInsuranceLabels(!!combination.isAccaInsuranceSelected),
      isSingleBetBetslip,
      hasStakeCaret: isStakeInputCaretEnabled,
      ...generosityProps,
    };
  };
};

export type DispatchProps = {
  dispatchStakeChange: ({ combinationIds, newValue }: { combinationIds: string[]; newValue?: number }) => void;
  dispatchStakeValidate: ({ combinationIds }: { combinationIds: string[] }) => void;
  dispatchStartPricePress: (combinationIds: string[], isSelected: boolean) => void;
  dispatchEachWayPress: (combinationIds: string[], isSelected: boolean) => void;
  dispatchBonusBallPress: (combinationIds: string[], isSelected: boolean) => void;
  dispatchAccaInsurancePress: (combinationIds: string[], isSelected: boolean) => void;
  dispatchPriceBoostPress: (combinationIds: string[], isSelected: boolean) => void;
  dispatchGenerosityPress: (combinationIds: string[], isSelected: boolean) => void;
  dispatchGenerosityWalletRemoveAction: (combinationIds: string[], type?: GenerosityWalletType) => void;
  dispatchGenerosityRemoveAction: () => void;
};

export const dispatchStartingPricePress = (
  dispatch: Dispatch,
  combinationIds: string[],
  isStartingPriceSelected: boolean,
) => {
  const isSelected = !isStartingPriceSelected;

  combinationIds.forEach((combinationId) => {
    dispatch<BettingSportsbookSPToggleAction>({
      type: BETTING__SBK_STARTING_PRICE_TOGGLE,
      payload: { combinationId, isSelected },
    });
  });
};

export const dispatchEachWayPress = (dispatch: Dispatch, combinationIds: string[], isEachWaySelected: boolean) => {
  const isSelected = !isEachWaySelected;

  combinationIds.forEach((combinationId) => {
    dispatch<BettingSportsbookEachWayToggleAction>({
      type: BETTING__SBK_EACH_WAY_TOGGLE,
      payload: { combinationId, isSelected },
    });
  });

  dispatch<BetslipSportsbookEachWayToggleAction>({
    type: UI__BETSLIP_SBK_EACH_WAY_TOGGLE,
    payload: { isSelected },
  });
};

export const mapDispatchToProps: MapDispatchToProps<DispatchProps, ContainerProps> = (dispatch: Dispatch) => ({
  dispatchStartPricePress: (combinationIds, isStartingPriceSelected) =>
    dispatchStartingPricePress(dispatch, combinationIds, isStartingPriceSelected),
  dispatchEachWayPress: (combinationIds, isEachWaySelected) =>
    dispatchEachWayPress(dispatch, combinationIds, isEachWaySelected),
  dispatchBonusBallPress: (combinationIds, isBonusBallSelected) => {
    const isSelected = !isBonusBallSelected;

    combinationIds.forEach((combinationId) => {
      dispatch<BettingSportsbookBonusBallToggleAction>({
        type: BETTING__SBK_BONUS_BALL_TOGGLE,
        payload: {
          combinationId,
          isSelected,
        },
      });
    });

    dispatch<BetslipSportsbookBonusBallToggleAction>({
      type: UI__BETSLIP_SBK_BONUS_BALL_TOGGLE,
      payload: {
        isSelected,
      },
    });
  },
  dispatchAccaInsurancePress: (combinationIds, isAccaInsuranceSelected) => {
    const isSelected = !isAccaInsuranceSelected;

    combinationIds.forEach((combinationId) => {
      dispatch<BettingSportsbookAccaInsuranceToggleAction>({
        type: BETTING__SBK_ACCA_INSURANCE_TOGGLE,
        payload: {
          combinationId,
        },
      });
    });

    dispatch<BetslipSportsbookAccaInsuranceToggleAction>({
      type: UI__BETSLIP_SBK_ACCA_INSURANCE_TOGGLE,
      payload: {
        isSelected,
      },
    });
  },
  dispatchStakeChange: ({ combinationIds, newValue }: { combinationIds: string[]; newValue?: number }): void => {
    combinationIds.forEach((combinationId) => {
      dispatch<BetslipSportsbookStakeInputChange>({
        type: UI__BETSLIP_SBK_STAKE_INPUT_CHANGE,
        payload: { combinationId, stake: newValue },
      });

      dispatch<BettingSportsbookUpdateCombinationStakeAction>({
        type: BETTING__SBK_UPDATE_COMBINATION_STAKE_ACTION,
        payload: { combinationId, stake: newValue },
      });
    });
  },

  dispatchStakeValidate: ({ combinationIds }: { combinationIds: string[] }): void => {
    combinationIds.forEach((combinationId) =>
      dispatch<BettingSportsbookValidateStake>({
        type: BETTING__SBK_VALIDATE_STAKE,
        payload: { combinationId },
      }),
    );
  },
  dispatchPriceBoostPress: (combinationIds, isPriceBoostSelected) => {
    const isSelected = !isPriceBoostSelected;

    combinationIds.forEach((combinationId) => {
      dispatch<BettingSportsbookPriceBoostToggleAction>({
        type: BETTING__SBK_PRICE_BOOST_TOGGLE,
        payload: {
          combinationId,
        },
      });
    });

    dispatch<BetslipSportsbookPriceBoostToggleAction>({
      type: UI__BETSLIP_SBK_PRICE_BOOST_TOGGLE,
      payload: {
        isSelected,
      },
    });
  },

  dispatchGenerosityPress: (combinationIds, isSelected) => {
    emitBetslipGenerosityWalletRequested({ combinationIds, isSelected });
  },
  dispatchGenerosityWalletRemoveAction: (combinationIds: string[], type?: GenerosityWalletType) => {
    combinationIds.forEach((combinationId) => {
      if (type === "BONUS_CASH") {
        dispatch<BettingSportsbookRemoveAllCombinationWalletsAction>({
          type: BETTING__SPORTSBOOK_REMOVE_ALL_COMBINATION_FREE_BETS_WALLETS_ACTION,
          payload: { combinationId },
        });
        return;
      }

      if (type === "ACCA_INSURANCE_TOKEN") {
        dispatch<BettingSportsbookAccaInsuranceToggleAction>({
          type: BETTING__SBK_ACCA_INSURANCE_TOGGLE,
          payload: { combinationId },
        });
        return;
      }

      if (type === "PRICE_BOOST_TOKEN") {
        dispatch<BettingSportsbookPriceBoostToggleAction>({
          type: BETTING__SBK_PRICE_BOOST_TOGGLE,
          payload: { combinationId },
        });
        return;
      }

      if (type === "MONEY_BACK_TOKEN") {
        dispatch<BettingSportsbookMoneyBackToggleAction>({
          type: BETTING__SBK_MONEY_BACK_TOGGLE,
          payload: { combinationId },
        });
        return;
      }

      if (type === "GHOST_LEG_TOKEN") {
        dispatch<BettingSportsbookGhostLegToggleAction>({
          type: BETTING__SBK_GHOST_LEG_TOGGLE,
          payload: { combinationId },
        });
      }
    });
  },
  dispatchGenerosityRemoveAction: () => {
    dispatch<BettingSportsbookRemoveGenerosityWalletsAction>({
      type: UI__GENEROSITY_WALLET_REMOVE_CLICK,
    });
  },
});
